import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { ArrowBack as BackIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { PageListLayout } from '../components/layouts/PageListLayout';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { agentFunctionService } from '../services/agentFunctionService';
import { useNotificationStore } from '../store/notificationStore';
import {
  AgentFunctionRequestStatus,
  type AgentFunctionRequestDto,
  type AgentFunctionRequestFilters,
} from '../types/agentFunction';

const getStatusChip = (status: number) => {
  const map: Record<number, { label: string; color: 'default' | 'warning' | 'success' | 'error' }> = {
    0: { label: 'Pending', color: 'default' },
    1: { label: 'Processing', color: 'warning' },
    2: { label: 'Completed', color: 'success' },
    3: { label: 'Failed', color: 'error' },
  };
  const cfg = map[status] ?? { label: 'Unknown', color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
};

const truncate = (str: string, maxLen: number) => {
  if (!str) return '—';
  return str.length <= maxLen ? str : `${str.slice(0, maxLen)}...`;
};

export const AgentFunctionRequestsListPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [requests, setRequests] = useState<AgentFunctionRequestDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<AgentFunctionRequestFilters>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await agentFunctionService.getAgentFunctionRequests(id, filters);
      setRequests(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load execution history';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [id, filters, addNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = (row: AgentFunctionRequestDto) => {
    navigate(`/agent-functions/requests/${row.id}`);
  };

  const tableColumns: DataTableColumn<AgentFunctionRequestDto>[] = [
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (row) => getStatusChip(row.status),
    },
    {
      id: 'inputJson',
      label: 'Input',
      minWidth: 200,
      render: (row) => truncate(row.inputJson || '', 80),
    },
    {
      id: 'outputJson',
      label: 'Output',
      minWidth: 200,
      render: (row) => truncate(row.outputJson || '', 80),
    },
    {
      id: 'durationMs',
      label: 'Duration (ms)',
      minWidth: 100,
      align: 'right',
      render: (row) => (row.durationMs != null ? row.durationMs : '—'),
    },
    {
      id: 'createdAt',
      label: 'Created At',
      minWidth: 170,
      render: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleString() : '—',
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View Detail">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/agent-functions/requests/${row.id}`);
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Execution History"
      breadcrumbs={[
        { label: 'Agent Functions', href: '/agent-functions' },
        { label: id || 'Function', href: id ? `/agent-functions/${id}` : undefined },
        { label: 'Execution History' },
      ]}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              size="small"
              onClick={() => navigate(id ? `/agent-functions/${id}` : '/agent-functions')}
              aria-label="Back"
            >
              <BackIcon />
            </IconButton>
          </Box>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={
                filters.status === undefined
                  ? ''
                  : filters.status.toString()
              }
              label="Status"
              onChange={(e) => {
                const value = e.target.value as string;
                setFilters({
                  ...filters,
                  status: value === '' ? undefined : (parseInt(value, 10) as AgentFunctionRequestStatus),
                  pageNumber: 1,
                });
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="0">Pending</MenuItem>
              <MenuItem value="1">Processing</MenuItem>
              <MenuItem value="2">Completed</MenuItem>
              <MenuItem value="3">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={requests.length === 0 && !loading}
      emptyState={{
        title: 'No execution history',
        description: 'Executions for this agent function will appear here.',
        action: id
          ? {
              label: 'Back to Function',
              onClick: () => navigate(`/agent-functions/${id}`),
            }
          : undefined,
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages: Math.max(
          1,
          Math.ceil(totalCount / (filters.pageSize || 10))
        ),
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={requests}
        loading={false}
        onRowClick={handleRowClick}
      />
    </PageListLayout>
  );
};
